import React from 'react';
import styled from 'styled-components';

import type { RewardsActivity } from 'src/gql/graphql';
import { formatTransactionDate } from './dpos-utils';

type RewardsActivityProps = {
  rewardsActivity: Array<RewardsActivity>;
  isPoints: boolean;
  formatValueForDisplay: (value: number, label: string, isLoyaltyPoints: boolean) => string;
};

export const DposRewardsActivityList = ({
  isPoints = false,
  rewardsActivity = [],
  formatValueForDisplay,
}: RewardsActivityProps): JSX.Element => (
  <RewardsList>
    {rewardsActivity.length > 0 ? (
      rewardsActivity.map(({ transactionType, orderType, transactionDateUtc, location, points, totalPaid }) => (
        <Reward key={transactionDateUtc} $points={points}>
          <DescriptionBlock>
            <Description>
              {transactionType}
              <OrderType> - {orderType}</OrderType>
            </Description>
            <Date>
              {formatTransactionDate(transactionDateUtc ?? '')} {location ? `- ${location}` : null}
            </Date>
            {totalPaid ? <TotalPaidMobile>{formatValueForDisplay(totalPaid, '', false)}</TotalPaidMobile> : null}
          </DescriptionBlock>
          <CostInPointsOrCashMobile $points={points}>
            {points && points > 0 ? '+' : null}
            {formatValueForDisplay(points ?? 0, 'pts', isPoints)}
          </CostInPointsOrCashMobile>

          {totalPaid ? <TotalPaid>{formatValueForDisplay(totalPaid, '', false)}</TotalPaid> : null}
          <CostInPointsOrCash $points={points}>
            {points && points > 0 ? '+' : null}
            {formatValueForDisplay(points ?? 0, 'pts', isPoints)}
          </CostInPointsOrCash>
        </Reward>
      ))
    ) : (
      <NoActivityAvailable>No rewards activity at this time</NoActivityAvailable>
    )}
  </RewardsList>
);

const RewardsList = styled.dl`
  list-style: none;
  margin-top: 0;
`;

const Description = styled.dt`
  font-weight: bold;
`;

const OrderType = styled.span`
  font-weight: normal;
`;

const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 45%;
`;

const CostInPointsOrCashMobile = styled.dd<{ $points?: number | null }>`
  color: ${({ $points, theme }) => ($points > 0 ? theme.colors.primaryGreen : theme.colors.red[60])};
  display: none;
`;

const TotalPaidMobile = styled.dd`
  display: none;
`;

const CostInPointsOrCash = styled.dd<{ $points?: number | null }>`
  color: ${({ $points, theme }) => ($points > 0 ? theme.colors.primaryGreen : theme.colors.red[60])};
  margin: 0;
  align-content: center;
  font-weight: bold;
  font-size: 13px;
`;

const TotalPaid = styled.dd`
  margin: 0;
  align-content: center;
  font-weight: normal;
  font-size: 13px;
`;

const Date = styled.dd`
  color: #828a8f;
  margin: 0;
  font-size: 13px;
`;

const NoActivityAvailable = styled.div`
  color: #828a8f;
`;

const Reward = styled.div<{ $points: string }>`
  margin-top: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  display: flex;
  gap: 50px;
  padding: 10px 0;
  justify-content: space-between;

  @media only screen and (min-width: 100px) and (max-width: 599px) {
    ${DescriptionBlock} {
      display: flex;
      width: 65%;
    }
    ${CostInPointsOrCashMobile} {
      color: ${({ $points, theme }) => ($points > 0 ? theme.colors.primaryGreen : theme.colors.red[60])};
      display: block;
      justify-content: end;
      margin: 0;
      align-content: center;
      font-weight: bold;
      font-size: 13px;
    }
    ${CostInPointsOrCash} {
      display: none;
    }
    ${TotalPaid} {
      display: none;
    }
    ${TotalPaidMobile} {
      margin: 0;
      align-content: center;
      font-weight: normal;
      font-size: 13px;
      display: flex;
      margin-top: 8px;
    }
  }
`;
